import qs from "qs";
import {
  SourceFilter,
  SplitTransaction,
  Transaction,
  TransactionResponse,
  Transactions,
  TxnAccountType,
  TxnCategories,
} from "types/Models/books";
import { FileObject } from "types/Models/fileObject";
import { CSVParsedResponse, CSVResponse } from "types/Models/reconciliation";
import { emptyApi } from "./emptyApi";
import { Invoice, SuggestedInvoice } from "types/Models/invoice";
import { Pagination } from "types/Models/pagination";
import { store } from "store/store";
import { RECURRING_JOUNRAL_ENTRY_UPDATE_TYPES_T } from "types/contants/recurringJournalEntryUpdateTypes";
import { Category } from "types/Models/payroll";

type FilterValue = {
  value: boolean | string | null | string[];
};
export type TransactionQuery = {
  groupId: string;
  entityId: string;
  page_num?: number;
  searchTerm?: string | null;
  transactionIds?: string;
  startDate?: FilterValue;
  endDate?: FilterValue;
  fromIds?: FilterValue;
  manualEntry?: FilterValue;
  bankStatement?: FilterValue;
  vendors?: FilterValue;
  categoryIds?: FilterValue;
  uncategorized?: FilterValue;
  cashFlow?: FilterValue;
  minAmount?: string;
  maxAmount?: string;
  hasMemo?: FilterValue;
  hasComments?: FilterValue;
  hasInvoices?: FilterValue;
  sortCol?: "AMOUNT" | "DATE" | "VENDOR" | "DESCRIPTION";
  sortOrder?: "ASC" | "DSC";
  hasNoVendors?: FilterValue;
  splitTransactions?: FilterValue;
  fetch_page_num?: boolean;
  jump_txn_id?: string;
  reviewed?: FilterValue;
  notReviewed?: FilterValue;
  txnAccountType?: TxnAccountType;
  pageSize?: string | null;
  hasInvoiceOrMemo?: FilterValue;
  messageId?: string;
  txnStatus?: "EXCLUDED";
  bankTransactions?: FilterValue;
  show_only_1099_txn?: boolean;
  classification?: FilterValue;
  noClassification?: FilterValue;
  classes?: FilterValue;
  include_split_txns?: boolean;
  excludeFullyApplied?: boolean;
};

export type TransactionFilterSet = Omit<
  TransactionQuery,
  | "page_num"
  | "transactionIds"
  | "fetch_page_num"
  | "sortCol"
  | "sortOrder"
  | "jump_txn_id"
  | "pageSize"
  | "messageId"
> & {
  filter_operation:
    | "CATEGORIZE"
    | "UNCATEGORIZE"
    | "ASSIGN_VENDOR"
    | "EXCLUDE"
    | "ASSIGN_CLASSIFICATION";
  excluded_txns_list?: string[];
};

export type SmartReviewTransaction = {
  id: string;
  date: string;
  description: string;
  amount: string;
  merchant: {
    id: string;
    name: string;
    logo: string | null;
  } | null;
  category: {
    uuid: string;
    name: string;
    identifier: number;
  } | null;
  categorisation_status: string;
  from: {
    bank_account: {
      uuid: string;
      nickname: string;
      mask: string;
      bank_brand: {
        name: string;
        logo_url: string | null;
        connection_provider: string;
      } | null;
    };
    source: string;
  } | null;
};

export type SmartReviewVendor = {
  merchant_data_id: string | null;
  vendor_name: string | null;
  vendor_logo: string | null;
  transaction_count: number;
  category_id: string | null;
  category_name: string | null;
  transactions: SmartReviewTransaction[];
};

export type SmartReviewBankTransferGroup = {
  group_number: number;
  transactions: SmartReviewTransaction[];
};

type SmartReviewVendorSection = {
  total_count: number;
  vendors: SmartReviewVendor[];
};

type SmartReviewBankTransferSection = {
  total_count: number;
  bank_transfer_groups: SmartReviewBankTransferGroup[];
};

export type SmartReviewResponse = {
  default: SmartReviewVendorSection;
  unmatched_vendor: SmartReviewVendorSection;
  uncategorised: SmartReviewVendorSection;
  bank_transfer: SmartReviewBankTransferSection;
};

export type MappedInvoiceResponse = {
  transactions: Transactions[];
  invoices: Invoice[] | null;
  invoice_discount_sum: {
    amount: number;
    category: string;
  };
  invoice_tax_sum: {
    amount: number;
    category: string;
  };
  adjustments: {
    fx_data_adjustment: {
      category: string;
      fx_difference: number;
    };
    manual_adjustment: {
      category: string;
      amount: number;
    };
  };
  partial_payment: {
    category: Category;
    amount: number;
    uuid: string;
  };
};

export const transactionFilterValuesToParams = (filters: TransactionQuery) => {
  const conditionalPageSize = Math.max(
    filters.transactionIds?.split(",").length || 0,
    20
  );

  return qs.stringify(
    {
      page_num: filters.page_num,
      search_term: filters.searchTerm,
      transaction_ids: filters.transactionIds,
      start_date: filters.startDate?.value,
      end_date: filters.endDate?.value,
      journal_entry: filters.manualEntry?.value,
      bank_statement: filters.bankStatement?.value,
      from_ids: (filters.fromIds?.value as string[])?.join(","),
      merchants: (filters.vendors?.value as string[])?.join(","),
      category_ids: (filters.categoryIds?.value as string[])?.join(","),
      uncategorized: filters.uncategorized?.value,
      cash_flow: filters.cashFlow?.value,
      min_amount: filters.minAmount,
      max_amount: filters.maxAmount,
      has_memo: filters.hasMemo?.value,
      has_comments: filters.hasComments?.value,
      has_invoices: filters.hasInvoices?.value,
      has_no_merchants: filters.hasNoVendors?.value,
      has_split_transactions: filters.splitTransactions?.value,
      sort_col: filters.sortCol,
      sort_order: filters.sortOrder,
      reviewed: filters.reviewed?.value,
      not_reviewed: filters.notReviewed?.value,
      fetch_page_num: filters.fetch_page_num,
      jump_txn_id: filters.jump_txn_id,
      txn_account_type: filters.txnAccountType || null,
      page_size: filters.pageSize || conditionalPageSize,
      has_invoice_or_memo: filters.hasInvoiceOrMemo?.value,
      message_id: filters.messageId,
      txn_status: filters.txnStatus,
      is_bank_txn: filters.bankTransactions?.value || null,
      show_only_1099_txn: filters.show_only_1099_txn || false,
      transaction_group_ids: (filters.classification?.value as string[])?.join(
        ","
      ),
      class_ids: (filters.classes?.value as string[])?.join(","),
      fetch_ungrouped_txn_ids: (
        filters.noClassification?.value as string[]
      )?.join(","),
      include_split_txns: filters.include_split_txns,
      exclude_fully_applied: filters.excludeFullyApplied,
    },
    {
      filter: (prefix, value) =>
        value === false || value === "" ? undefined : value,
      skipNulls: true,
    }
  );
};

type SuggestedLinkTransactionsResponse = {
  [key: string]: Transactions[];
};

const findCategoryInTree = (
  categories: TxnCategories[],
  categoryId: string
): TxnCategories | undefined => {
  for (const category of categories) {
    if (category.uuid === categoryId) {
      return category;
    }
    const foundInChildren =
      category.types && findCategoryInTree(category.types, categoryId);
    if (foundInChildren) {
      return foundInChildren;
    }
  }
  return undefined;
};

const isChartOfAccountsData = (
  data: unknown
): data is { types: TxnCategories[] } => {
  return Boolean(data) && Array.isArray((data as { types?: unknown }).types);
};

const findCachedCategoryById = (
  categoryId?: string | null
): Transaction["category"] | undefined => {
  if (!categoryId) {
    return undefined;
  }

  const queries = store.getState().api.queries;

  for (const queryData of Object.values(queries)) {
    if (
      queryData?.endpointName !== "getChartOfAccounts" ||
      !isChartOfAccountsData(queryData.data)
    ) {
      continue;
    }

    const category = findCategoryInTree(queryData.data.types, categoryId);

    if (category) {
      return {
        uuid: category.uuid,
        name: category.name,
        identifier: category.identifier,
      };
    }
  }

  return undefined;
};

export const patchTransactionInCaches = (
  dispatch: typeof store.dispatch,
  transactionId: string,
  updateDraft: (draft: Transaction) => void
) => {
  const queries = store.getState().api.queries;
  const patchResults: { undo: () => void }[] = [];

  Object.entries(queries).forEach(([, queryData]) => {
    if (queryData?.endpointName === "getTransactionData") {
      patchResults.push(
        dispatch(
          transactionApi.util.updateQueryData(
            "getTransactionData",
            // @ts-ignore
            queryData.originalArgs,
            (draft) => {
              if (draft.transaction.uuid === transactionId) {
                updateDraft(draft.transaction);
              }
            }
          )
        )
      );
    }

    if (queryData?.endpointName === "getAllTransactionsData") {
      patchResults.push(
        dispatch(
          transactionApi.util.updateQueryData(
            "getAllTransactionsData",
            // @ts-ignore
            queryData.originalArgs,
            (draft) => {
              const txn = draft.transactions.find(
                (txn) => txn.transaction.uuid === transactionId
              );
              if (txn) {
                updateDraft(txn.transaction);
              }
            }
          )
        )
      );
    }
  });

  return patchResults;
};

export const transactionApi = emptyApi.injectEndpoints({
  endpoints: (build) => ({
    getAllTransactionsData: build.query<TransactionResponse, TransactionQuery>({
      query: (query) => {
        const { groupId, entityId } = query;

        let queryUrl = transactionFilterValuesToParams(query);

        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/entity_transactions/`,
          params: {
            ...qs.parse(queryUrl),
          },
        };
      },
      providesTags: (result) => (result ? ["BOOKS_TRANSACTIONS"] : []),
    }),

    getTransactionsFilterSet: build.query<
      {
        txns_count: number;
        previously_categorized_txns_count: number;
        vendor_assigned_txns_count: number;
        categorized_txns_with_linked_transaction_count: number;
        split_txns_count?: number;
        invoice_linked_txns_count?: number;
        txns_hash_key: string;
      },
      TransactionFilterSet
    >({
      query: ({
        groupId,
        entityId,
        searchTerm,
        startDate,
        endDate,
        fromIds,
        manualEntry,
        bankStatement,
        vendors,
        categoryIds,
        uncategorized,
        cashFlow,
        minAmount,
        maxAmount,
        hasMemo,
        hasComments,
        hasInvoices,
        hasNoVendors,
        splitTransactions,
        reviewed,
        notReviewed,
        txnAccountType,
        hasInvoiceOrMemo,
        txnStatus,
        bankTransactions,
        filter_operation,
        excluded_txns_list,
        classification,
        noClassification,
        classes,
      }) => {
        let queryUrl = qs.stringify(
          {
            search_term: searchTerm,
            start_date: startDate?.value,
            end_date: endDate?.value,
            journal_entry: manualEntry?.value,
            bank_statement: bankStatement?.value,
            //@ts-ignore
            from_ids: fromIds?.value?.join(","),
            //@ts-ignore
            merchants: vendors?.value?.join(","),
            //@ts-ignore
            category_ids: categoryIds?.value?.join(","),
            uncategorized: uncategorized?.value,
            cash_flow: cashFlow?.value,
            min_amount: minAmount,
            max_amount: maxAmount,
            has_memo: hasMemo?.value,
            has_comments: hasComments?.value,
            has_invoices: hasInvoices?.value,
            has_no_merchants: hasNoVendors?.value,
            has_split_transactions: splitTransactions?.value,
            reviewed: reviewed?.value,
            not_reviewed: notReviewed?.value,
            txn_account_type: txnAccountType || null,
            has_invoice_or_memo: hasInvoiceOrMemo?.value,
            txn_status: txnStatus,
            is_bank_txn: bankTransactions?.value || null,
            filter_operation,
            excluded_txns_list: excluded_txns_list?.join(","),
            transaction_group_ids: Array.isArray(classification?.value)
              ? classification.value.join(",")
              : null,
            class_ids: Array.isArray(classes) ? classes.join(",") : null,
            fetch_ungrouped_txn_ids: Array.isArray(noClassification)
              ? noClassification.join(",")
              : null,
          },
          {
            filter: (prefix, value) =>
              value === false || value === "" ? undefined : value,
            skipNulls: true,
          }
        );
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/entity_transactions/filter_set/`,
          params: {
            ...qs.parse(queryUrl),
          },
        };
      },
      providesTags: (result) => (result ? ["BOOKS_TRANSACTIONS"] : []),
    }),

    getTransactionData: build.query<
      Transactions,
      {
        groupId: string;
        entityId: string;
        transactionId: string;
      }
    >({
      query: ({ groupId, entityId, transactionId }) => {
        let queryUrl = qs.stringify(
          { transaction_id: transactionId },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/entity_transaction/${queryUrl}`,
        };
      },
      providesTags: (result) => (result ? ["BOOKS_SINGLE_TRANSACTION"] : []),
    }),

    editTransaction: build.mutation<
      Transactions,
      {
        groupId: string;
        entityId: string;
        transactionId: string;
        date?: string;
        amount?: string;
        exchange_rate?: string;
        account_id?: string;
        description?: string;
        merchant_data_id?: string;
        transaction_category_id?: string;
        invoice_mapping_id?: string;
        invoice_status?: string;
      }
    >({
      query: ({ groupId, entityId, transactionId, ...restPayload }) => {
        const formData = new FormData();
        Object.entries(restPayload).forEach((payload) =>
          formData.append(payload[0], payload[1])
        );

        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/entity_transaction/${transactionId}/`,
          method: "PATCH",
          body: formData,
        };
      },
      invalidatesTags: (result) =>
        result
          ? [
              "BOOKS_TRANSACTIONS",
              "BOOKS_SINGLE_TRANSACTION",
              "GENERAL_LEDGER",
              "LIVE_REPORT",
              "NOTES",
              "CATEGORISATION_HISTORY",
              "CATEGORISATION_HISTORY_TXNS",
            ]
          : [],
      async onQueryStarted(
        { transactionId, date, amount, description },
        { dispatch, queryFulfilled }
      ) {
        const hasOptimisticUpdate =
          date !== undefined ||
          amount !== undefined ||
          description !== undefined;

        const patchResults = hasOptimisticUpdate
          ? patchTransactionInCaches(dispatch, transactionId, (draft) => {
              if (date !== undefined) {
                draft.date = date;
              }
              if (amount !== undefined) {
                draft.amount = Number(amount);
              }
              if (description !== undefined) {
                draft.description = description;
              }
            })
          : [];

        try {
          const { data: updatedPost } = await queryFulfilled;

          patchTransactionInCaches(dispatch, transactionId, (draft) => {
            Object.assign(draft, updatedPost.transaction);
          });
        } catch {
          patchResults.forEach((patchResult) => patchResult.undo());
        }
      },
    }),

    deleteTransaction: build.mutation<
      void,
      {
        groupId: string;
        entityId: string;
        transactionId: string;
      }
    >({
      query: ({ groupId, entityId, transactionId }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/entity_transaction/`,
          method: "DELETE",
          params: {
            transaction_id: transactionId,
          },
        };
      },
      invalidatesTags: (result) =>
        result
          ? [
              "BOOKS_TRANSACTIONS",
              "GENERAL_LEDGER",
              "JOURNAL_ENTRIES",
              "NOTES",
              "VENDORS",
              "RECONCILIATION",
              "CATEGORISATION_HISTORY",
              "CATEGORISATION_HISTORY_TXNS",
            ]
          : [],
    }),

    updateTransaction: build.mutation<
      Transactions,
      {
        groupId: string;
        entityId: string;
        previewTxnId: string;
        payload: {
          transaction_category_uuid?: string | null;
          memo?: string | null;
          linked_transaction_uuid?: string | null;
          ai_category_confirmation?: boolean;
          invoice_mapping_id?: string;
          invoice_status?: "AUTO_MATCH_CONFIRM";
          amount?: number;
          exchange_rate?: number;
        };
      }
    >({
      query: ({ groupId, entityId, previewTxnId, payload }) => {
        let queryUrl = qs.stringify(
          {
            transaction_id: previewTxnId,
          },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/entity_transaction/${queryUrl}`,
          method: "PUT",
          body: payload,
        };
      },
      invalidatesTags: (result) =>
        result
          ? [
              "GENERAL_LEDGER",
              "LIVE_REPORT",
              "BOOKS_TRANSACTIONS",
              "NOTES",
              "VENDORS",
              "OPEN_ITEMS",
              "RECONCILIATION",
              "CATEGORISATION_HISTORY_TXNS",
              "CATEGORISATION_HISTORY",
            ]
          : [],
      async onQueryStarted(
        { previewTxnId, payload },
        { dispatch, queryFulfilled }
      ) {
        const hasOptimisticUpdate =
          "transaction_category_uuid" in payload ||
          "memo" in payload ||
          "amount" in payload ||
          "exchange_rate" in payload ||
          "linked_transaction_uuid" in payload ||
          "ai_category_confirmation" in payload;

        const patchResults = hasOptimisticUpdate
          ? patchTransactionInCaches(dispatch, previewTxnId, (draft) => {
              if ("transaction_category_uuid" in payload) {
                const optimisticCategory = findCachedCategoryById(
                  payload.transaction_category_uuid
                );
                draft.category = optimisticCategory || undefined;

                if (!payload.ai_category_confirmation) {
                  draft.categorisation_status = optimisticCategory
                    ? "MANUAL_CATEGORISED"
                    : "NOT_CATEGORISED";
                }
              }
              if ("memo" in payload) {
                draft.memo = payload.memo || undefined;
              }
              if ("amount" in payload && payload.amount !== undefined) {
                draft.amount = payload.amount;
              }
              if (
                "exchange_rate" in payload &&
                payload.exchange_rate !== undefined
              ) {
                draft.exchange_rate = payload.exchange_rate;
              }
              if ("linked_transaction_uuid" in payload) {
                draft.linked_transaction =
                  payload.linked_transaction_uuid || undefined;
              }
              if (
                payload.ai_category_confirmation &&
                draft.categorisation_status === "AI_CATEGORISED"
              ) {
                draft.categorisation_status = "AI_CATEGORISED_ACCEPTED";
              }
            })
          : [];

        try {
          const { data: updatedPost } = await queryFulfilled;

          patchTransactionInCaches(dispatch, previewTxnId, (draft) => {
            Object.assign(draft, updatedPost.transaction);
          });
          dispatch(transactionApi.util.invalidateTags(["LINKED_INVOICES"]));
        } catch {
          patchResults.forEach((patchResult) => patchResult.undo());
        }
      },
    }),

    addTransactionComment: build.mutation<
      Transactions,
      { groupId: string; previewTxnId: string; payload: any }
    >({
      query: ({ groupId, previewTxnId, payload }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/transaction/${previewTxnId}/comment/`,
          method: "POST",
          body: payload,
        };
      },
      invalidatesTags: (result) =>
        result
          ? [
              "BOOKS_TRANSACTIONS",
              "BOOKS_SINGLE_TRANSACTION",
              "GENERAL_LEDGER",
              "CATEGORISATION_HISTORY_TXNS",
            ]
          : [],
    }),

    addTransactionInvoice: build.mutation<
      Transactions,
      { groupId: string; previewTxnId: string; files: any }
    >({
      query: ({ groupId, previewTxnId, files }) => {
        const formData = new FormData();
        files.forEach((file: File) => {
          formData.append("invoices", file);
        });
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/transaction/${previewTxnId}/invoice/`,
          method: "POST",
          body: formData,
        };
      },
      invalidatesTags: (result) =>
        result
          ? [
              "BOOKS_TRANSACTIONS",
              "BOOKS_SINGLE_TRANSACTION",
              "GENERAL_LEDGER",
              "CATEGORISATION_HISTORY_TXNS",
            ]
          : [],
    }),

    deleteTransactionInvoice: build.mutation<
      Transactions,
      { groupId: string; previewTxnId: string; payload: any }
    >({
      query: ({ groupId, previewTxnId, payload }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/transaction/${previewTxnId}/invoice/`,
          method: "DELETE",
          body: payload,
        };
      },
      invalidatesTags: (result) =>
        result
          ? [
              "BOOKS_TRANSACTIONS",
              "BOOKS_SINGLE_TRANSACTION",
              "GENERAL_LEDGER",
              "CATEGORISATION_HISTORY_TXNS",
            ]
          : [],
    }),

    getAllMerchants: build.query<
      {
        merchants: string[];
      } & Pagination,
      {
        groupId: string;
        entityId: string;
        page_num?: number;
        search_term?: string | null;
      }
    >({
      query: ({ groupId, entityId, page_num, search_term }) => {
        let queryUrl = qs.stringify(
          { page_num, search_term: search_term || null },
          { skipNulls: true, addQueryPrefix: true }
        );
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/merchant_filter/${queryUrl}`,
        };
      },
    }),

    getAllAccount: build.query<
      SourceFilter[],
      {
        groupId: string;
        entityId: string;
        account_type?: "STRIPE" | "EXCLUDE_STRIPE" | "ALL";
      }
    >({
      query: ({ groupId, entityId, account_type }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/account_filter/`,
          params: { account_type },
        };
      },
    }),

    pushToChat: build.mutation<
      { message_id: string },
      {
        groupId: string;
        payload: {
          channel_url: string;
          custom_type: string;
          transaction_ids: string;
          message?: string;
        };
        entityId: string;
      }
    >({
      query: ({ groupId, payload, entityId }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/push_to_chat/`,
          method: "POST",
          body: payload,
        };
      },
    }),

    uploadCSVTransaction: build.mutation<
      CSVParsedResponse,
      {
        groupId: string;
        accountId: string;
        csv: File;
        entityId: string;
      }
    >({
      query: ({ groupId, accountId, entityId, csv }) => {
        const formData = new FormData();
        formData.append("account_id", accountId);
        formData.append("csv", csv);

        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/manual-transaction/csv/`,
          method: "POST",
          body: formData,
        };
      },
    }),

    populateCSVTransactions: build.mutation<
      CSVResponse[],
      {
        groupId: string;
        entityId: string;
        date_field: string;
        description_field: string;
        merchant_field: string;
        amount_field: string;
        credit_field: string;
        debit_field: string;
        memo_field: string;
        bank_csv_id: string;
        date_format: string;
        category_field: string;
      }
    >({
      query: ({ groupId, entityId, ...restPayload }) => {
        const formData = new FormData();
        Object.entries(restPayload).forEach((payload) =>
          formData.append(payload[0], payload[1])
        );

        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/manual-transaction/csv/`,
          method: "PUT",
          body: formData,
        };
      },
      invalidatesTags: (result) =>
        result ? ["BOOKS_TRANSACTIONS", "VENDORS"] : [],
    }),

    getTransactionBulkTemplate: build.query<
      { download_url: string },
      { entityId: string }
    >({
      query: ({ entityId }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/templates/transactions/`,
      }),
    }),

    uploadManualTransactionInvoice: build.mutation<
      FileObject,
      {
        groupId: string;
        entityId: string;
        invoice: File;
      }
    >({
      query: ({ groupId, entityId, invoice }) => {
        const formData = new FormData();
        formData.append("invoice", invoice);
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/manual-transaction/invoice/`,
          method: "PUT",
          body: formData,
        };
      },
    }),

    addManualTransactions: build.mutation<
      FileObject,
      {
        groupId: string;
        entityId: string;
        accountId: string;
        transactions: {
          date: string;
          description: string;
          amount: number;
          original_amount?: number;
          exchange_rate?: number;
          currency?: string;
          merchant_data_id: string;
          transaction_category_id: string;
          invoice_id: string | null;
        }[];
      }
    >({
      query: ({ groupId, entityId, accountId, transactions }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/manual-transaction/`,
          method: "POST",
          body: { account_id: accountId, transactions },
        };
      },
      invalidatesTags: (result) =>
        result ? ["BOOKS_TRANSACTIONS", "VENDORS", "RECONCILIATION"] : [],
    }),

    categorizeGroupTransactions: build.mutation<
      Transactions,
      {
        groupId: string;
        entityId: string;
        payload: {
          transaction_ids?: string;
          merchant_data_uuid?: string;
          transaction_category_uuid: string | null;
          previous_assigned_category_id?: string | null;
          transactions_hash_key?: string;
          link_transaction_dict?: Record<string, string>;
        };
      }
    >({
      query: ({ groupId, entityId, payload }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/entity_transactions/`,
          method: "PUT",
          body: payload,
        };
      },
      invalidatesTags: (result) =>
        result
          ? [
              "BOOKS_TRANSACTIONS",
              "VENDORS",
              "CATEGORISATION_HISTORY",
              "CATEGORISATION_HISTORY_TXNS",
              "SMART_REVIEW",
            ]
          : [],
    }),
    splitTransactions: build.mutation<
      Transactions,
      {
        groupId: string;
        entityId: string;
        transactionId: string;
        transactions: {
          description: string;
          amount: number;
          merchant_id?: string | null;
          category_id: string;
          invoice_id: string | null;
          linked_transaction_uuid?: string | null;
        }[];
      }
    >({
      query: ({ groupId, entityId, transactionId, transactions }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/transaction/${transactionId}/split_transactions/`,
          method: "POST",
          body: { split_transactions: transactions },
        };
      },
      invalidatesTags: (result) =>
        result ? ["BOOKS_TRANSACTIONS", "VENDORS"] : [],
    }),
    updateSplitTransactions: build.mutation<
      Transactions,
      {
        groupId: string;
        entityId: string;
        transactionId: string;
        transactions: {
          description: string;
          amount: number;
          merchant_id?: string | null;
          category_id: string;
          invoice_id: string | null;
        }[];
      }
    >({
      query: ({ groupId, entityId, transactionId, transactions }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/transaction/${transactionId}/split_transactions/`,
          method: "PUT",
          body: { split_transactions: transactions },
        };
      },
      invalidatesTags: (result) =>
        result
          ? ["BOOKS_TRANSACTIONS", "BOOKS_SINGLE_TRANSACTION", "VENDORS"]
          : [],
    }),
    deleteSplitTransactions: build.mutation<
      Transactions,
      {
        groupId: string;
        entityId: string;
        transactionId: string;
      }
    >({
      query: ({ groupId, entityId, transactionId }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/transaction/${transactionId}/split_transactions/`,
          method: "DELETE",
        };
      },
      invalidatesTags: [
        "BOOKS_TRANSACTIONS",
        "BOOKS_SINGLE_TRANSACTION",
        "VENDORS",
      ],
    }),
    getAllSplitTransactions: build.query<
      SplitTransaction[],
      {
        groupId: string;
        entityId: string;
        transactionId: string;
      }
    >({
      query: ({ groupId, entityId, transactionId }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/transaction/${transactionId}/split_transactions/`,
        };
      },
    }),

    updateMultipleTransactionsData: build.mutation<
      Transaction[],
      {
        groupId: string;
        entityId: string;
        ai_category_confirmation?: boolean;
        transactions_hash_key?: string;
        transactions?: {
          uuid: string;
          category_id?: string;
          merchant_id?: string;
          description?: string;
        }[];
      }
    >({
      query: ({
        groupId,
        entityId,
        transactions,
        ai_category_confirmation,
        transactions_hash_key,
      }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/transactions_update/`,
          method: "PUT",
          body: {
            transactions,
            ai_category_confirmation,
            transactions_hash_key,
          },
        };
      },
      invalidatesTags: (result) =>
        result
          ? [
              "MONEY_IN_TRANSIT",
              "BOOKS_TRANSACTIONS",
              "VENDORS",
              "CATEGORISATION_HISTORY",
              "CATEGORISATION_HISTORY_TXNS",
              "SMART_REVIEW",
            ]
          : [],
    }),

    bulkAssignMerchant: build.mutation<
      {
        total_transactions_updated: number;
        transactions_updated: {
          merchant_data: {
            name: string;
            logo: string;
            uuid: string;
          };
          transaction_uuid: string;
        }[];
      },
      {
        groupId: string;
        entityId: string;
        payload: {
          transaction_ids?: string;
          entity_merchant_uuid: string;
          transactions_hash_key?: string;
        };
      }
    >({
      query: ({ groupId, entityId, payload }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/merchants_assign/`,
          method: "PUT",
          body: payload,
        };
      },
      invalidatesTags: (result) =>
        result
          ? [
              "BOOKS_TRANSACTIONS",
              "VENDORS",
              "CATEGORISATION_HISTORY",
              "CATEGORISATION_HISTORY_TXNS",
            ]
          : [],
    }),

    refreshTransactions: build.mutation<
      void,
      {
        groupId: string;
        entityId: string;
        from_date?: string;
        to_date?: string;
        sources?: string[];
      }
    >({
      query: ({ groupId, entityId, from_date, to_date, sources }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/refresh_transactions/`,
          method: "PUT",
          body: {
            from_date: from_date || null,
            to_date: to_date || null,
            entity_bank_account_ids: sources || null,
          },
        };
      },
      invalidatesTags: (result) =>
        result ? ["BOOKS_TRANSACTIONS", "VENDORS"] : [],
    }),

    mapInkleTransactionToInvoice: build.mutation<
      null,
      {
        entityId: string;
        entityInvoiceIds: string;
        transactionId: string;
        transactions?:
          | {
              amount: number;
              description: string | undefined;
              category_id: string | undefined;
            }[]
          | null;
        writeOffRemainingAt?: string;
      }
    >({
      query: ({
        entityId,
        entityInvoiceIds,
        transactionId,
        transactions,
        writeOffRemainingAt,
      }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/entity_transaction/${transactionId}/invoice/map/`,
          method: "PUT",
          body: {
            entity_invoice_ids: entityInvoiceIds,
            transactions,
            write_off_remaining_at: writeOffRemainingAt,
          },
        };
      },
      invalidatesTags: () => [
        "BOOKS_TRANSACTIONS",
        "BOOKS_SINGLE_TRANSACTION",
        "GENERAL_LEDGER",
        "LIVE_REPORT",
        "LINKED_INVOICES",
        "INVOICES",
      ],
    }),

    mapInkleTransactionToBill: build.mutation<
      null,
      {
        entityId: string;
        entityBillIds: string;
        transactionId: string;
        transactions?:
          | {
              amount: number;
              description: string | undefined;
              category_id: string | undefined;
              bill_ids?: string[];
            }[]
          | null;
      }
    >({
      query: ({ entityId, entityBillIds, transactionId, transactions }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/entity_transaction/${transactionId}/bill/map/`,
          method: "PUT",
          body: { entity_bills_ids: entityBillIds, transactions },
        };
      },
      invalidatesTags: () => [
        "BOOKS_TRANSACTIONS",
        "BOOKS_SINGLE_TRANSACTION",
        "GENERAL_LEDGER",
        "LIVE_REPORT",
        "LINKED_INVOICES",
        "VENDOR_BILL",
      ],
    }),

    getMappedBill: build.query<
      MappedInvoiceResponse,
      { entityId: string; transactionId: string }
    >({
      query: ({ entityId, transactionId }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/entity_transaction/${transactionId}/bill/map/`,
        };
      },
      providesTags: () => ["LINKED_INVOICES"],
    }),

    getMappedInvoice: build.query<
      MappedInvoiceResponse,
      { entityId: string; transactionId: string }
    >({
      query: ({ entityId, transactionId }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/entity_transaction/${transactionId}/invoice/map/`,
        };
      },
      providesTags: () => ["LINKED_INVOICES"],
    }),

    refreshReceipts: build.mutation<
      void,
      {
        groupId: string;
        entityId: string;
        payload: {
          from_date: string;
          to_date: string;
          entity_bank_account_ids?: string[];
        };
      }
    >({
      query: ({ groupId, entityId, payload }) => {
        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/refresh_receipts/`,
          method: "PUT",
          body: payload,
        };
      },
    }),

    changeTransactionStatus: build.mutation<
      void,
      {
        entityId: string;
        payload: {
          transaction_ids?: string[];
          transaction_status: "EXCLUDED" | "RESTORED";
          transactions_hash_key?: string;
          excluded_from_reconciliation?: boolean;
        };
      }
    >({
      query: ({ entityId, payload }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/txn_status/`,
          method: "PUT",
          body: payload,
        };
      },
      invalidatesTags: (result) =>
        result
          ? [
              "BOOKS_TRANSACTIONS",
              "BOOKS_SINGLE_TRANSACTION",
              "GENERAL_LEDGER",
              "LIVE_REPORT",
              "VENDORS",
              "RECONCILIATION",
            ]
          : [],
      async onQueryStarted({ payload }, { dispatch, queryFulfilled }) {
        const patchResults = (payload.transaction_ids || []).flatMap(
          (transactionId) =>
            patchTransactionInCaches(dispatch, transactionId, (draft) => {
              draft.status = payload.transaction_status;
            })
        );

        try {
          await queryFulfilled;
        } catch {
          patchResults.forEach((patchResult) => patchResult.undo());
        }
      },
    }),

    getSuggestionsToLinkTransactions: build.query<
      SuggestedLinkTransactionsResponse,
      {
        entityId: string;
        payload: {
          category_id: string;
          transaction_ids: string[];
        };
      }
    >({
      query: ({ entityId, payload }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/suggest_link_transactions_bulk/`,
          method: "POST",
          body: payload,
        };
      },
    }),

    runCategorizationEngine: build.mutation<
      void,
      {
        entityId: string;
        start_date: string;
      }
    >({
      query: ({ entityId, start_date }) => {
        return {
          url: `/api/inkle/bookkeeping/entity/${entityId}/classification_pipeline/`,
          method: "POST",
          body: { start_date: start_date },
        };
      },
    }),

    getSmartReview: build.query<
      SmartReviewResponse,
      {
        groupId: string;
        entityId: string;
        start_date: string;
        end_date: string;
      }
    >({
      query: ({ groupId, entityId, start_date, end_date }) => ({
        url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/smart-review/`,
        params: { start_date, end_date },
      }),
      providesTags: ["SMART_REVIEW"],
    }),

    unlinkInvoices: build.mutation<
      void,
      {
        entityId: string;
        transactionId: string;
        groupId: string;
        financial_record_type: string;
        invoice_id?: string;
      }
    >({
      query: ({
        entityId,
        transactionId,
        financial_record_type,
        groupId,
        invoice_id,
      }) => {
        const body = invoice_id
          ? { invoice_id, financial_record_type }
          : { financial_record_type };

        return {
          url: `/api/inkle/bookkeeping/group/${groupId}/entity/${entityId}/entity_transaction/${transactionId}/unlink/`,
          method: "PATCH",
          body,
        };
      },
      invalidatesTags: () => [
        "BOOKS_TRANSACTIONS",
        "BOOKS_SINGLE_TRANSACTION",
        "GENERAL_LEDGER",
        "LIVE_REPORT",
        "INVOICES",
        "VENDOR_BILL",
        "LINKED_INVOICES",
      ],
    }),

    getSuggestedInvoices: build.query<
      { suggested_invoices: SuggestedInvoice[] },
      { entityId: string; transactionId: string }
    >({
      query: ({ entityId, transactionId }) => ({
        url: `/api/inkle/bookkeeping/entity/${entityId}/entity_transaction/${transactionId}/suggested_invoices/`,
      }),
      providesTags: [
        "LINKED_INVOICES",
        "BOOKS_TRANSACTIONS",
        "BOOKS_SINGLE_TRANSACTION",
      ],
    }),
  }),
});

export const {
  useGetAllTransactionsDataQuery,
  useLazyGetAllTransactionsDataQuery,
  useGetTransactionDataQuery,
  useUpdateTransactionMutation,
  useAddTransactionCommentMutation,
  useAddTransactionInvoiceMutation,
  useDeleteTransactionInvoiceMutation,
  useGetAllMerchantsQuery,
  useLazyGetAllMerchantsQuery,
  useGetAllAccountQuery,
  usePushToChatMutation,
  useUploadCSVTransactionMutation,
  usePopulateCSVTransactionsMutation,
  useUploadManualTransactionInvoiceMutation,
  useAddManualTransactionsMutation,
  useEditTransactionMutation,
  useDeleteTransactionMutation,
  useCategorizeGroupTransactionsMutation,
  useSplitTransactionsMutation,
  useDeleteSplitTransactionsMutation,
  useUpdateSplitTransactionsMutation,
  useGetAllSplitTransactionsQuery,
  useUpdateMultipleTransactionsDataMutation,
  useBulkAssignMerchantMutation,
  useRefreshTransactionsMutation,
  useMapInkleTransactionToInvoiceMutation,
  useRefreshReceiptsMutation,
  useChangeTransactionStatusMutation,
  useMapInkleTransactionToBillMutation,
  useGetMappedBillQuery,
  useGetMappedInvoiceQuery,
  useGetTransactionsFilterSetQuery,
  useLazyGetTransactionsFilterSetQuery,
  useGetSuggestionsToLinkTransactionsQuery,
  usePrefetch: useTransactionApisPrefetch,
  useRunCategorizationEngineMutation,
  useUnlinkInvoicesMutation,
  useLazyGetTransactionBulkTemplateQuery,
  useGetSmartReviewQuery,
  useLazyGetSmartReviewQuery,
  useGetSuggestedInvoicesQuery,
} = transactionApi;
